import {
    Box,
    Grid,
    MenuItem,
    Paper,
    Typography,
    Tooltip,
    FormGroup,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    Client,
    Estimate as NewEstimate,
    FacilityLevelOfCareWithCrossoverDays,
    QuoteMethod,
    Vob,
    PayorPlan,
    Facility,
    Payor,
    Plan, RateAsPayorMap,
    Estimate
} from '@finpay-estimation/estimation-types';
import {
    LoadingOverlay,
    TextField,
    NewLOCSummary, 
    checkCrossoverAndUpdateLocs,
    DialogActionButton,
    saveCallbackStatus,
    Button
    // Vob
} from '@finpay-development/shared-components';
import _ from 'lodash';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { RootState } from '../../../shared/state/root-reducer';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Alert } from '../../../meta-data/models/metaData';
import { admissionsAdvisorUtils } from '../../../admissions-advisor/utils/admission-advisor-utils';
import { AppDispatch } from '../../../shared/state/store';
import { useIsDisabledAfterConvert } from '../../hooks/use-is-disabled-after-convert';

import { loadSelectedEstimate, saveSelectedEstimate, saveSelectedEstimateSelfPay, createSelectedEstimate, createSelectedEstimateSelfPay, loadSavedEstimatesListv3 } from '../../../shared/state/estimator-thunk';
import { estimatorSlice, setSelectedEstimate, clearSelectedEstimate } from '../../../shared/state/estimator-slice';
import { clearEncounter, setSelectedEncounter } from '../../../patient/state/patient-slice';
import { clearSelectedVob } from '../../../shared/state/vob-slice';
import { PAYMENT_TYPES } from '../../../patient/components/models/payment-type';
import { getAlerts } from '../../../meta-data/state/meta-data-thunk';
import { getPatientDemographics } from '../../../patient/state/patient-thunk';
import { loadPatientVobList } from '../../../shared/state/vob-thunk';
import { useVobData } from '../vob/vob-form/hooks/useVobData';
import { setPatient } from '../../../admissions-advisor/state/vob-patient-slice';
import { estimatorSchema } from '../../../shared/validation/schemas';
import { showErrorStatus, showStatus } from '../../../security/state/user-slice';
import { clearPaymentURLParams } from '../../../admissions-advisor/state/vob-slice';
import { quoteMethodToDisplayString, stringToQuoteMethod } from 'src/admissions-advisor/components/pfr-estimator/estimator-loc-modal/estimator-loc-modal';
import { EstimatorLevelOfCareModal } from './estimator-loc-modal-v3';
import { AdmissionDateV3 } from './admission-date-v3';
import { EstimatorPriorCareSummary } from './estimator-prior-care-summary-v3';
import { SidebarFinancialSummary } from './sidebar-financial-summary';
import { SidebarPlanYearCrossover } from './sidebar-plan-year-crossover';
import { SidebarTotalEstimatedPfr } from './sidebar-total-estimated-pfr';
import { SidebarPriorCareAdjustment } from './sidebar-prior-care-adjustment';
import { TakePaymentModal } from '../../../shared/components/take-payment-modal';
import { useParams, useNavigate } from 'react-router-dom';
import { loadSelectedVob } from '../../../shared/state/vob-thunk';
import { EstCompletePatientRecordModal } from './estimator-complete-patient-record-modal-v3';
import { patientService } from '../../../patient/services/patient-service';
import { getInstanceOfCare } from '../../../patient/state/patient-thunk';
import { PatientEncounter } from '../../../patient/components/models/patient-encounter';
import { loadAndSetRateAsPayorPlan } from '../../utils/payor-plan-utils';
import { isSelfPayPayor, loadAndSetRateAsWithoutVob, normalizeDescription } from '../../../shared/utils/insurance-utils';
import { EstimateValidationService } from '../../utils/estimate-validation';
import EstimatesSidebar from "../shared/estimates-sidebar";
import { Utils } from '../../../shared/utils';
import { getPlanDetails, getSelfPayPayer } from '../../../admissions-advisor/components/inbound/launchV2Utils';
import { getEstimatorFacilityPayers } from '../../../admissions-advisor/state/vob-thunk';

function EstimateTab() {
    const state = {
        clients: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    .filteredByIsActive
        ),
        allClients: useSelector(
            (state: RootState) =>
                state.implementationContext?.implementationSpecialistClient.allClientsMap
        ),
        isLoadingClients: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationSpecialistClient
                    .isLoading
        ),
        isLoadingFacilities: useSelector(
            (state: RootState) =>
                state.implementationContext.implementationFacility.isLoading
        ),
        vob: useSelector(
            (state: RootState) => state.vobContext.selectedVob
        ),
        vobPatientState: useSelector(
            (state: RootState) =>
                state.admissionsAdvisorContext.vobPatientContext
        ),
        vobPatient: useSelector(
            (state: RootState) =>
                state.admissionsAdvisorContext?.vobPatientContext.patient
        ),
        estimatorLoadingState: useSelector(
            (state: RootState) =>
                state.estimatorContext.isLoading
        ),
        clientAlerts: useSelector((state: RootState) => {
            return state.metaData.alerts;
        }),
        clientLevelsOfCareState: useSelector((state: RootState) => {
            return state.admissionsAdvisorContext.estimatorContext
                ?.clientLevelsOfCare;
        }),
        payors: useSelector(
            (state: RootState) =>
                state.adminContext.adminConfigurationContext?.payers
        ),
        masterLevelsOfCareState: useSelector((state: RootState) => {
            return state?.adminContext.adminConfigurationContext?.levelsOfCare;
        }),
        estimate: useSelector((state: RootState) => {
            return state?.estimatorContext.selectedEstimate;
        }),
        facilityLevelsOfCareList: useSelector((state: RootState) => {
            return state?.estimatorContext.facilityLevelsOfCareList;
        }),
        userProfile: useSelector(
            (state: RootState) => state.userContext.userProfile
        ),
        selectedPatient: useSelector((state: RootState) => {
            return state?.patientContext.selectedPatient;
        }),
        savedEstimations: useSelector(
            (state: RootState) =>
                state.estimatorContext.savedEstimatesList
        ),
        savedEstimatesLoading: useSelector(
            (state: RootState) =>
                state.estimatorContext.isLoading.savedEstimatesList
        ),
        patientVobList: useSelector(
            (state: RootState) => state.vobContext.patientVobList
        ),
        patientVobListLoading: useSelector(
            (state: RootState) => state.vobContext.isLoading?.patientVobList
        ),
        reduxRateAsPayorMap: useSelector((state: RootState) => state.estimatorContext.rateAsPayorMap) || {},
        reduxMasterPayors: useSelector((state: RootState) => state.adminContext.adminConfigurationContext?.payersMap) || new Map(),
        selectedEncounter: useSelector((state: RootState) => {
            return state?.patientContext.selectedEncounter;
        }),
        rateAsPayorPlan: useSelector((state: RootState) => {
            return state.estimatorContext.rateAsPayorPlan as PayorPlan;
        }),
        payers: useSelector((state: RootState) => {
            return state.adminContext.adminConfigurationContext?.payers;
        })
    };

    const {
        clients,
        allClients,
        isLoadingClients,
        vob,
        vobPatientState,
        vobPatient,
        clientAlerts,
        clientLevelsOfCareState,
        payors,
        estimate,
        facilityLevelsOfCareList,
        userProfile,
        selectedPatient,
        savedEstimations,
        savedEstimatesLoading,
        patientVobList,
        patientVobListLoading,
        selectedEncounter,
        estimatorLoadingState,
        rateAsPayorPlan,
        payers
    } = state;

    const [isLOCModalOpen, setIsLOCModalOpen] = useState(false);
    const [openPaymentModal, setOpenPaymentModal] = useState(false);
    const [paymentData, setPaymentData] = useState<any>(undefined);
    const [isCompletePatientModalOpen, setIsCompletePatientModalOpen] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState<number | ''>('');
    const [facilitiesLoading, setFacilitiesLoading] = useState(false);
    const [facilities, setFacilities] = useState<any>([]);
    const [payerAlerts, setPayerAlerts] = useState<Alert[]>([]);
    const [employerAlerts, setEmployerAlerts] = useState<Alert[]>([]);
    const [groupAlerts, setGroupAlerts] = useState<Alert[]>([]);
    const [prefixAlerts, setPrefixAlerts] = useState<Alert[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [isPrivatePay, setIsPrivatePay] = useState(false);
    const hasInitFromEstimate = useRef(false);
    const hasCheckedPaymentUrl = useRef(false);

    const [doesLOCSummaryValNeedUpdating, setDoesLOCSummaryValNeedUpdating] =
        useState(false);

    const dispatch = useDispatch<AppDispatch>();
    const { estimateId, patientId } = useParams<{ estimateId?: string, patientId?: string }>();
    const navigate = useNavigate();

    const selectedPatientCurrentInsurance = useSelector(
        (state: RootState) => state.patientContext.selectedPatientCurrentInsurance
    );

    const payersMap = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext.payersMap
    );
     // Check if patient has self-pay insurance
     const isSelfPay = useMemo(() => {
        return isSelfPayPayor(selectedPatientCurrentInsurance?.payorId, payersMap);
    }, [selectedPatientCurrentInsurance?.payorId, payersMap]);

    const isDisabledAfterConvert = useIsDisabledAfterConvert();
    
    const isIOCConverted = selectedEncounter && 
        selectedEncounter.patientEncounterId === estimate?.patientEncounterId &&
        (selectedEncounter.isConverted || selectedEncounter.workflow?.workflowId === 3);

    // Add state for dropdowns
    const [vobsList, setVobsList] = useState<any[]>([]);
    const [selectedVob, setSelectedVob] = useState<string>(vob?.vobId?.toString() || '');
    const [isLoadingEstimate, setIsLoadingEstimate] = useState(false);

    // Add ref to track if this is initial load
    const isInitialLoad = useRef(true);
    const isManualVobChange = useRef(false);
    const hasAutoSelectedFirstEstimate = useRef(false);
    const isLoadingIOCRef = useRef(false);
    const lastLoadedPatientIdRef = useRef<number | undefined>(undefined);
    const fetchingEncounterIdRef = useRef<number | string | null>(null);
    const lastRecalcSummaryPlanKeyRef = useRef<string>('');
    const paramId: number = -2;

    useEffect(() => {
        forceValidateOnload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // if the client is configured with the feature flag, use the new estimate values, otherwise use the estimator state values, otherwise use the vob values
    const currentClientId = estimate?.clientId
        ? estimate.clientId
        : vob?.client?.clientId
            ? vob.client.clientId
            : selectedPatient?.clientId;
    const currentFacilityId = estimate?.facilityId
        ? estimate.facilityId
        : vob?.facility?.facilityId;
    const initialAdmissionDate = useMemo(() => {
        if (estimate?.anticipatedAdmitDate) {
            // If we have an existing date, convert it to a Date object for the DatePicker
            return new Date(estimate.anticipatedAdmitDate);
        }
        console.log('init admit date', estimate);
        // For new estimates, use today's date
        return new Date();

    }, [estimate?.anticipatedAdmitDate]);
    const initialQuoteMethod = estimate?.quoteMethod
        ? estimate.quoteMethod
        : QuoteMethod.AVGLOS;

    const [admissionDate, setAdmissionDate] = useState(initialAdmissionDate);

    // Create facilities array for useVobData hook - memoized to prevent infinite re-renders
    const facilitiesArray = useMemo(() => {
        return currentClientId && allClients.get(Number(currentClientId))?.clientFacilities 
            ? allClients.get(Number(currentClientId))!.clientFacilities 
            : [];
    }, [currentClientId, allClients]);

    // Use shared VOB utilities
    const { getVobDisplayText } = useVobData({ 
        facilities: facilitiesArray,
        includeReduxVobs: true 
    });

    useEffect(() => {
        setAdmissionDate(initialAdmissionDate);
    }, [initialAdmissionDate]);

    // Fetch VOB if it's empty but estimate has vobId
    useEffect(() => {
        if (estimate?.vobId && (!vob || !vob.vobId)) {
            const fetchVob = async () => {
                try {
                    console.log('Fetching VOB with vobId:', estimate.vobId);
                    await dispatch(loadSelectedVob({ vobId: estimate.vobId }));
                    
                    // Note: rateAsPayorPlan will be set automatically by other effects once VOB data is available
                    
                    // After VOB loads, fetch saved estimates if this is initial load
                    if (isInitialLoad.current) {
                        try {
                            await dispatch(
                                loadSavedEstimatesListv3({
                                    paramId: paramId,
                                    patientId: Number(patientId),
                                })
                            );
                            isInitialLoad.current = false;
                        } catch (error) {
                            console.error('Error fetching saved estimates:', error);
                            dispatch(showErrorStatus('Error loading saved estimates. Please try again.'));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching VOB:', error);
                        dispatch(showErrorStatus('Error loading VOB data. Please try again.'));
                }
            };
            fetchVob();
        }
    }, [estimate?.vobId, vob?.vobId, dispatch]);

    // Note: Facility levels of care are now fetched by payor-plan-utils.ts when setting rateAsPayorPlan

    //to do : if estimate does not have a valid VOb then nav to vob tab

    // setting up clientAlerts
    // needs client id from vob and the meta data
    useEffect(() => {
        const clientId = vob?.client?.clientId;

        // possible that we are still loading vob
        if (!clientId) return;

        const alerts = clientAlerts[clientId];

        // if we have a client id and our client's alerts, set everything
        if (clientId && alerts) {
            const payerAlerts = clientAlerts[clientId].payer.filter(
                payerAlert => {
                    return payerAlert.payor_id === vob.payor.payorId;
                }
            );

            const groupAlerts = clientAlerts[clientId].group.filter(
                groupAlert => {
                    return groupAlert?.group_code === vob.groupNum;
                }
            );

            const employerAlerts = clientAlerts[clientId].employer.filter(
                employerAlert => {
                    return (
                        employerAlert.client_employer_id ===
                        vob.employer?.employerId
                    );
                }
            );

            let newPrefixAlerts: Alert[] = [];
            const prefixes = clientAlerts[clientId].prefix;

            for (let i = 0; i < prefixes.length; i++) {
                if (prefixes.length > 0) {
                    const prefixAlert = prefixes[i];
                    const subStringEndingIndex = prefixAlert.prefix!.length;

                    const policyNumberSubString = vob?.policyNum?.substring(
                        0,
                        subStringEndingIndex
                    );

                    const isAMatch =
                        policyNumberSubString === prefixAlert.prefix;
                    // match
                    if (isAMatch) {
                        // if nothing in the new alerts, add it
                        if (newPrefixAlerts.length === 0) {
                            newPrefixAlerts.push(prefixAlert);
                            continue;
                        }

                        // length is same as the first prefix
                        if (
                            newPrefixAlerts[0].prefix!.length ===
                            prefixAlert.prefix!.length
                        ) {
                            // add it the list, don't replace
                            newPrefixAlerts.push(prefixAlert);
                            continue;
                        }

                        // length is greater than the first prefix
                        if (
                            newPrefixAlerts[0].prefix!.length <
                            prefixAlert.prefix!.length
                        ) {
                            // replace with new alert
                            newPrefixAlerts = [prefixAlert];
                            continue;
                        }
                    }
                }
            }

            setPayerAlerts(payerAlerts);
            setGroupAlerts(groupAlerts);
            setPrefixAlerts(newPrefixAlerts);
            setEmployerAlerts(employerAlerts);
        } else if (clientId && !clientAlerts[clientId]) {
            // client id but no alerts, fetch the alerts
            dispatch(getAlerts(clientId));
        }
    }, [vob, clientAlerts]);

    useEffect(() => {
        // run once, the first time estimate becomes truthy
        if (!estimate || hasInitFromEstimate.current) return;
        hasInitFromEstimate.current = true;
        setIsPrivatePay(!!estimate.isPrivatePay);

    }, [estimate]);

    const setAdvisorPatientBody = async () => {
        if (vobPatientState.patient.fpPatientId) {
            const {payload}: {payload: any} = await dispatch(
                getPatientDemographics(vobPatientState.patient.fpPatientId!)
            );

            await dispatch(
                setPatient({
                    advisorPatientBody: {
                        email: payload.patientDetails.contact.email,
                        primaryAddress: {
                            streetAddress1:
                                payload.patientDetails?.contact?.primaryAddress
                                    ?.streetAddress1,
                            streetAddress2:
                                payload.patientDetails?.contact?.primaryAddress
                                    ?.streetAddress2,
                            city: payload.patientDetails?.contact
                                ?.primaryAddress?.city,
                            state: {
                                stateCode:
                                    payload.patientDetails.contact
                                        .primaryAddress?.state?.stateCode,
                            },
                            zipCode:
                                payload.patientDetails?.contact?.primaryAddress
                                    ?.zipCode,
                        },
                        phoneNumber:
                            payload.patientDetails?.contact?.primPhoneNum,
                    },
                })
            );
        }
    };

    useEffect(() => {
        setAdvisorPatientBody();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasCheckedPaymentUrl.current) return;
        
        const urlParams = new URLSearchParams(window.location.search);
        const opParam = urlParams.get('op');
        
        if (opParam === 'pay' && patientId && (selectedEncounter?.patientEncounterId || estimate?.patientEncounterId)) {
            setPaymentData({
                patientId: patientId,
                patientEncounterId: selectedEncounter?.patientEncounterId || estimate?.patientEncounterId,
                paymentType: PAYMENT_TYPES.DOWN_PAYMENT,
                paymentChannelId: userProfile.userRole.roleType === 'I' ? 5 : 3
            });
            setOpenPaymentModal(true);
            hasCheckedPaymentUrl.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimate, selectedEncounter, patientId]);

    async function handleEstimatorLevelOfCareModalClose() {
        console.log(
            '%cLOC Modal Close - Formik:',
            'background-color: #0199cc; color: white; font-weight: bold',
            createEstimatorFormik.values
        );
        console.log(
            '%cLOC Modal Close - Estimator State:',
            'background-color: #0199cc; color: white; font-weight: bold',
            estimate
        );
        console.log(
            '%cLOC Modal Open - Vob State:',
            'background-color: #0199cc; color: white; font-weight: bold',
            vob
        );
        console.log(
            '%cLOC Modal Open - Client LOCState:',
            'background-color: #0199cc; color: white; font-weight: bold',
            clientLevelsOfCareState
        );
        setIsLOCModalOpen(false);
        // update estimate list post save
        await dispatch(
            loadSavedEstimatesListv3({
                paramId: paramId,
                patientId: Number(patientId),
            })
        );
    }

    const estimatorValidationSchema = Yup.object(estimatorSchema);

    // formik for estimator
    const createEstimatorFormik = useFormik({
        initialValues: {
            client: currentClientId,
            facility: currentFacilityId,
            quoteMethod: initialQuoteMethod,
        },
        enableReinitialize: true,
        validationSchema: estimatorValidationSchema,
        onSubmit: () => {},
    });


    const handleNewEstimateQuoteMethodChange = async (e: Event) => {
        try {
            const selectedQuoteMethod = (e.target as HTMLInputElement)
                .value as unknown as QuoteMethod;

            if (estimate) {
                //estimate already exists

                let putEstimateBody: NewEstimate | undefined;

                switch (true) {
                    case selectedQuoteMethod === QuoteMethod.SCA: {
                        setDoesLOCSummaryValNeedUpdating(true);

                        putEstimateBody = {
                            vobId: vob?.vobId!,
                            patientEncounterId: estimate?.patientEncounterId,
                            clientId: estimate?.clientId,
                            facilityId: estimate?.facilityId,
                            advisorPatientId: estimate?.advisorPatientId || 1,
                            patientId: selectedPatient.patientId,
                            description: normalizeDescription(estimate?.description, estimate.isPrivatePay || isPrivatePay, isSelfPay),
                            quoteMethod: selectedQuoteMethod,
                            anticipatedAdmitDate: new Date(admissionDate),
                            facilityLevelOfCare: estimate.facilityLevelOfCare,
                            priorCare: estimate.priorCare,
                            isPlanYearCrossover: estimate.isPlanYearCrossover,
                            crossoverSelection: estimate?.crossoverSelection,
                            isActive: estimate.isActive,
                            rateAsPayorPlan: rateAsPayorPlan,
                            isSelfPay: isSelfPay,
                            isPrivatePay: estimate.isPrivatePay || isPrivatePay,
                        };

                        break;
                    }

                    case selectedQuoteMethod === QuoteMethod.MANUAL: {
                        setDoesLOCSummaryValNeedUpdating(true);

                        const updatedFacilityLevelOfCare =
                            estimate?.facilityLevelOfCare?.map(facLOC => {
                                const foundFacLOCFromList =
                                    facilityLevelsOfCareList?.find(
                                        facLOCFromList => {
                                            return (
                                                facLOC.facilityLevelOfCareId ==
                                                facLOCFromList.facilityLevelOfCareId
                                            );
                                        }
                                    );

                                if (foundFacLOCFromList) {
                                    return {
                                        ...facLOC,
                                        pdrRate: foundFacLOCFromList.pdrRate,
                                    };
                                } else {
                                    throw new Error(
                                        'Could not find facility level of care from newFacilityLevelsOfCareList Redux state'
                                    );
                                }
                            });

                        putEstimateBody = {
                            vobId: vob?.vobId!,
                            patientEncounterId: estimate?.patientEncounterId,
                            clientId: estimate?.clientId,
                            facilityId: estimate?.facilityId,
                            advisorPatientId: estimate?.advisorPatientId || 1,
                            patientId: selectedPatient.patientId,
                            description: normalizeDescription(estimate?.description, estimate.isPrivatePay || isPrivatePay, isSelfPay),
                            quoteMethod: selectedQuoteMethod,
                            anticipatedAdmitDate: new Date(admissionDate),
                            facilityLevelOfCare: updatedFacilityLevelOfCare,
                            priorCare: estimate.priorCare,
                            isPlanYearCrossover: estimate.isPlanYearCrossover,
                            crossoverSelection: estimate?.crossoverSelection,
                            isActive: estimate.isActive,
                            rateAsPayorPlan: rateAsPayorPlan,
                            isSelfPay: isSelfPay,
                            isPrivatePay: estimate.isPrivatePay || isPrivatePay,
                        };

                        break;
                    }

                    case [QuoteMethod.AVGLOS, QuoteMethod.RLOS].includes(
                        selectedQuoteMethod
                    ): {
                        // Any quote method -> avlos/rlos

                        let updatedFacilityLevelOfCare =
                            estimate?.facilityLevelOfCare?.map(facLOC => {
                                const foundFacLOCFromList =
                                    facilityLevelsOfCareList?.find(
                                        facLOCFromList => {
                                            return (
                                                facLOC.facilityLevelOfCareId ==
                                                facLOCFromList.facilityLevelOfCareId
                                            );
                                        }
                                    );

                                if (foundFacLOCFromList) {
                                    return {
                                        ...facLOC,
                                        losDays:
                                            selectedQuoteMethod ===
                                            QuoteMethod.AVGLOS
                                                ? foundFacLOCFromList.losDays
                                                : foundFacLOCFromList.recommendedLos,
                                        pdrRate: foundFacLOCFromList.pdrRate,
                                    };
                                } else {
                                    throw new Error(
                                        'Could not find facility level of care from newFacilityLevelsOfCareList Redux state'
                                    );
                                }
                            });

                        const { isCrossover, newSelectedLocs } = (isSelfPay || isPrivatePay)
                            ? {
                                isCrossover: false,
                                newSelectedLocs: updatedFacilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
                              }
                            : checkCrossoverAndUpdateLocs({
                                selectedLocs:
                                    updatedFacilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
                                admissionDate: new Date(admissionDate),
                                policyEndDate: new Date(vob?.policyEndDate!),
                        });

                        /**
                         * Because the endpoint doesn't allow request bodies with missing losDaysBeforeCrossover/losDaysAfterCrossover properties if isPlanYearCrossover === true,
                         * we will set isPlanYearCrossover to false and prompt the user to update from the modal
                         * */

                        promptUserToUpdateModalForCrossover =
                            admissionsAdvisorUtils.isModalUpdateNeededForCrossover(
                                {
                                    isCrossover,
                                    newSelectedLocs,
                                    policyEndDate: new Date(vob?.policyEndDate!),
                                    anticipatedAdmissionDate: new Date(
                                        admissionDate
                                    ),
                                }
                            );

                        putEstimateBody = {
                            vobId: vob?.vobId!,
                            patientEncounterId: estimate?.patientEncounterId,
                            clientId: estimate?.clientId,
                            facilityId: estimate?.facilityId,
                            advisorPatientId: estimate?.advisorPatientId || 1,
                            patientId: selectedPatient.patientId,
                            description: normalizeDescription(estimate?.description, estimate.isPrivatePay || isPrivatePay, isSelfPay),
                            quoteMethod: selectedQuoteMethod,
                            anticipatedAdmitDate: new Date(admissionDate),
                            facilityLevelOfCare:
                                promptUserToUpdateModalForCrossover
                                    ? updatedFacilityLevelOfCare
                                    : newSelectedLocs,
                            priorCare: estimate.priorCare,
                            isPlanYearCrossover:
                                promptUserToUpdateModalForCrossover
                                    ? false
                                    : isCrossover,
                            crossoverSelection: estimate?.crossoverSelection,
                            isActive: estimate.isActive,
                            summarySelection: estimate?.summarySelection,
                            rateAsPayorPlan: rateAsPayorPlan,
                            isSelfPay: isSelfPay,
                            isPrivatePay: estimate.isPrivatePay || isPrivatePay,
                        };

                        break;
                    }

                    default:
                }

                if (putEstimateBody) {
                    await dispatch(
                        (isSelfPay || isPrivatePay)
                          ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                          : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                    );
                    
                    // Refresh the saved estimates list to show updated description
                    await dispatch(loadSavedEstimatesListv3({
                        paramId: paramId,
                        patientId: Number(patientId),
                    }));
                }
            }
        } catch (e) {
            console.error(e);
            dispatch(
                showErrorStatus('Error updating quote method and Level of Care')
            );
        }
    };

    let promptUserToUpdateModalForCrossover: boolean = false;
    
    // Only calculate crossover if we have a valid VOB with policyEndDate
    if (vob?.policyEndDate && estimate?.facilityLevelOfCare) {
        const crossoverResult = (isSelfPay || isPrivatePay)
            ? {
                isCrossover: false,
                newSelectedLocs: estimate?.facilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
              }
            : checkCrossoverAndUpdateLocs({
                selectedLocs: estimate?.facilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
                admissionDate: new Date(admissionDate),
                policyEndDate: new Date(vob.policyEndDate),
            });
    
        promptUserToUpdateModalForCrossover = admissionsAdvisorUtils.isModalUpdateNeededForCrossover({
            ...crossoverResult,
            policyEndDate: new Date(vob.policyEndDate),
            anticipatedAdmissionDate: new Date(admissionDate),
        });
    }

    
    const handleAdmissionDateOnChange = async (date: Date) => {
        try {
            const selectedAdmissionDate = new Date(date);
            setAdmissionDate(selectedAdmissionDate);

            if (estimate) {
                //estimate already exists
                const { isCrossover, newSelectedLocs } = (isSelfPay || isPrivatePay)
                    ? {
                        isCrossover: false,
                        newSelectedLocs: estimate.facilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
                    }
                    : checkCrossoverAndUpdateLocs({
                        selectedLocs: estimate.facilityLevelOfCare as FacilityLevelOfCareWithCrossoverDays[],
                        admissionDate: selectedAdmissionDate,
                        policyEndDate: new Date(vob?.policyEndDate!),
                    });

                /**
                 * Because the endpoint doesn't allow request bodies with missing losDaysBeforeCrossover/losDaysAfterCrossover properties if isPlanYearCrossover === true,
                 * we will set isPlanYearCrossover to false and prompt the user to update from the modal
                 * */

                promptUserToUpdateModalForCrossover =
                    admissionsAdvisorUtils.isModalUpdateNeededForCrossover({
                        isCrossover,
                        newSelectedLocs,
                        policyEndDate: new Date(vob?.policyEndDate!),
                        anticipatedAdmissionDate: selectedAdmissionDate,
                    });

                let putEstimateBody = {
                    ..._.omit(estimate, [
                        'estimateId',
                        'createUserId',
                        'createDt',
                        'lastUpdateUserId',
                        'lastUpdateDt',
                        'summary',
                        'crossOverSummary',
                        'priorCareSummary',
                    ]),
                    patientId: selectedPatient.patientId,
                    vobId: vob?.vobId!,
                    advisorPatientId: estimate?.advisorPatientId || 1,
                    anticipatedAdmitDate: selectedAdmissionDate,
                    description: normalizeDescription(estimate?.description, estimate.isPrivatePay || isPrivatePay, isSelfPay),
                    facilityLevelOfCare: promptUserToUpdateModalForCrossover
                        ? estimate.facilityLevelOfCare
                        : newSelectedLocs,
                    isPlanYearCrossover: promptUserToUpdateModalForCrossover
                        ? false
                        : isCrossover,
                    rateAsPayorPlan: rateAsPayorPlan,
                    isSelfPay: isSelfPay,
                    isPrivatePay: estimate.isPrivatePay || isPrivatePay,
                };

                await dispatch(
                    (isSelfPay || isPrivatePay)
                      ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                      : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                );
                
                // Refresh the saved estimates list to show updated description
                await dispatch(loadSavedEstimatesListv3({
                    paramId: paramId,
                    patientId: Number(patientId),
                }));
            }
        } catch (e) {
            console.error(e);
            dispatch(
                showErrorStatus('Error updating Anticipated Admission Date')
            );
        }
    };

    const forceValidateOnload = () => {
        setTimeout(() => {
            createEstimatorFormik.validateForm(); // unlock the IOC
        }, 500);
    };


    const handlePaymentModalCancel = () => {
        setOpenPaymentModal(false);
        dispatch(clearPaymentURLParams());
    };

    const quoteMethodValueAsEnum =
        typeof createEstimatorFormik.values.quoteMethod === 'string'
            ? stringToQuoteMethod(createEstimatorFormik.values.quoteMethod)
            : createEstimatorFormik.values.quoteMethod;
    const quoteMethodValueAsDisplayString =
        quoteMethodToDisplayString(initialQuoteMethod);

    const currentClient = currentClientId && allClients ? 
        allClients.get(Number(currentClientId)) : undefined;

    const currentFacility = currentClientId && currentFacilityId && allClients && currentClient ? 
        currentClient.clientFacilities?.find(facility => 
            facility.facilityId === currentFacilityId
        ) : undefined;

    useEffect(() => {
        // Only fetch estimates when we have a VOB loaded and it wasn't loaded from estimate (i.e., tab navigation)
        if (vob?.vobId && !estimate?.vobId && isInitialLoad.current) {
            const fetchSavedEstimates = async () => {
                try {
                    await dispatch(
                        loadSavedEstimatesListv3({
                            paramId: paramId,
                            patientId: Number(patientId),
                        })
                    );
                    // Mark initial load as complete
                    isInitialLoad.current = false;
                } catch (error) {
                    console.error('Error fetching saved estimates:', error);
                    dispatch(showErrorStatus('Error loading saved estimates. Please try again.'));
                }
            };
            fetchSavedEstimates();
        }
    }, [vob?.vobId, estimate?.vobId, dispatch]);

    useEffect(() => {
        if (isSelfPay || isPrivatePay) {
            const fetchFacilities = async () => {
                setFacilitiesLoading(true);
                try {
                    const clientId = Number(selectedPatient?.clientId);
                    const targetClient = allClients?.get(clientId);
                    if (targetClient?.clientFacilities) {
                        setFacilities(targetClient.clientFacilities);
                        if (estimate?.facilityId) {
                            setSelectedFacility(Number(estimate?.facilityId));
                        } else {
                            if (targetClient.clientFacilities.length > 0) {
                                const facilityId = targetClient.clientFacilities[0].facilityId;
                                setSelectedFacility(facilityId);
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error fetching facilities:', err);
                } finally {
                    setFacilitiesLoading(false);
                }
            };
            fetchFacilities();
        }
    }, [isSelfPay, isPrivatePay]);

    // Derive the selected estimate value from current state
    const selectedEstimateValue = estimateId || estimate?.estimateId?.toString() || '';

    useEffect(() => {
        if (
            !estimateId &&
            !hasAutoSelectedFirstEstimate.current &&
            savedEstimations &&
            savedEstimations.length > 0 &&
            !estimate &&
            !savedEstimatesLoading &&
            !isLoadingEstimate &&
            selectedPatient?.patientId
        ) {
            const firstEstimate = (savedEstimations as any[])[0];
            const firstEstimateId = firstEstimate?.pfrEstimateId || firstEstimate?.estimateId;
            
            if (firstEstimateId) {
                hasAutoSelectedFirstEstimate.current = true;
                setIsLoadingEstimate(true);

                navigate(`/patients/${selectedPatient.patientId}/estimate/${firstEstimateId}`, { replace: true });

                dispatch(
                    loadSelectedEstimate({
                        paramId: paramId,
                        estimationId: Number(firstEstimateId)
                    })
                ).finally(() => {
                    setIsLoadingEstimate(false);
                });
            }
        }
    }, [estimateId, savedEstimations, estimate, savedEstimatesLoading, isLoadingEstimate, selectedPatient, dispatch, navigate, paramId]);

    // Fetch Patient VOBs if empty
    useEffect(() => {
        if (patientId && (!patientVobList || patientVobList.length === 0)) {
            dispatch(loadPatientVobList(Number(patientId)));
        }
    }, [patientId, dispatch]);

    // Fetch available VOBs for dropdown and select the appropriate VOB
    useEffect(() => {
        if (patientVobList && patientVobList.length > 0) {
            // Filter valid VOBs and map them to dropdown format using shared utilities
            const formattedVobs = patientVobList
                .filter(patientVob => patientVob.isValid === true)
                .map(patientVob => ({
                    id: patientVob.vobId || 0,
                    name: getVobDisplayText(patientVob)
                }));
            
            setVobsList(formattedVobs);
            
    
            if (!isManualVobChange.current) {
                // Set the VOB that's linked to the selected estimate
                if (estimate?.vobId) {
                    setSelectedVob(estimate.vobId.toString());
                }
                // If no estimate.vobId, then use current vob if available
                else if (vob?.vobId) {
                    setSelectedVob(vob.vobId.toString());
                }
                // Otherwise select the first one
                else if (formattedVobs.length > 0) {
                    setSelectedVob(formattedVobs[0].id.toString());
                }
            }
        } else if (vob && vob.vobId) {
            // Fallback to current VOB if patientVobList isn't populated
            setVobsList([{
                id: vob.vobId,
                name: getVobDisplayText(vob)
            }]);
    
            if (!isManualVobChange.current) {
                setSelectedVob(vob.vobId.toString());
            }
        }
    }, [patientVobList, vob, estimate, getVobDisplayText]);

    useEffect(() => {
        if (
            !estimate?.estimateId ||
            !estimate?.facilityLevelOfCare?.length ||
            !facilityLevelsOfCareList?.length ||
            !rateAsPayorPlan?.plan?.facilityPlanId ||
            !vob?.vobId ||
            !selectedPatient?.patientId
        ) return;

        const planKey = `${estimate.estimateId}-${rateAsPayorPlan.plan.facilityPlanId}`;
        if (lastRecalcSummaryPlanKeyRef.current === planKey) return;

        const synced = estimate.facilityLevelOfCare.map((loc) => {
            const fromList = facilityLevelsOfCareList.find(
                (l) => l.facilityLevelOfCareId === loc.facilityLevelOfCareId || l.levelOfCareId === loc.facilityLevelOfCareId
            );
            if (!fromList) return loc;
            if (loc.losDays === fromList.losDays && loc.pdrRate === fromList.pdrRate) return loc;
            return { ...loc, losDays: fromList.losDays ?? loc.losDays, pdrRate: fromList.pdrRate ?? loc.pdrRate };
        });
        const hasChanges = synced.some((loc, i) => {
            const orig = estimate!.facilityLevelOfCare![i];
            return orig.losDays !== loc.losDays || orig.pdrRate !== loc.pdrRate;
        });
        if (!hasChanges) {
            lastRecalcSummaryPlanKeyRef.current = planKey;
            return;
        }

        lastRecalcSummaryPlanKeyRef.current = planKey;
        const saveAction = isSelfPay || isPrivatePay ? saveSelectedEstimateSelfPay : saveSelectedEstimate;
        const putBody = {
            ..._.omit(estimate, [
                'estimateId', 'createUserId', 'createDt', 'lastUpdateUserId', 'lastUpdateDt',
                'summary', 'crossOverSummary', 'priorCareSummary',
            ]),
            patientId: selectedPatient.patientId,
            vobId: vob.vobId,
            facilityLevelOfCare: synced,
            rateAsPayorPlan,
            advisorPatientId: estimate.advisorPatientId || 1,
            isSelfPay: isSelfPay,
            isPrivatePay: estimate.isPrivatePay || isPrivatePay,
        };
        dispatch(saveAction({ estimate: putBody as Estimate, estimateId: estimate.estimateId }));
    }, [estimate, facilityLevelsOfCareList, rateAsPayorPlan, vob?.vobId, selectedPatient?.patientId, isSelfPay, isPrivatePay, dispatch]);

    // Handle Facility on Estimate only for SelfPay
    const handleFacilityChange = async (facilityId: number) => {
        setSelectedFacility(facilityId);

        let updatedEstimate: Estimate = {
            ...Utils.deepClone(estimate),
            facilityId: facilityId,
        };
        // dispatch(estimatorSlice.actions.setRateAsPayorPlan(null));

        const selectedFacility: Facility = {
            facilityId: facilityId
        }

        let selectedPayor: Payor;
        let selectedPlan: Plan;

        if (isPrivatePay || isSelfPay) {
            const sssconfig: any = {
                paramId: paramId,
                masterListPayers: payers,
                facilityId: facilityId,
            };
            const facilityPayors = await dispatch(getEstimatorFacilityPayers(sssconfig));
            const selfPayPayor = getSelfPayPayer(facilityPayors);
            const planDetails = getPlanDetails(selfPayPayor, facilityPayors);
            
            selectedPayor = {
                payorId: selfPayPayor?.cfgPayorId
            } as Payor;
            
            selectedPlan = {
                payorPlanId: planDetails?.payorPlanId
            } as Plan;
        } else {
            selectedPayor = {
                payorId: selectedPatientCurrentInsurance?.payorId
            } as Payor;
            
            selectedPlan = {
                payorPlanId: selectedPatientCurrentInsurance?.payorPlanId
            } as Plan;
        }

        // call the rateasPayor w/ new facility
        await loadAndSetRateAsPayorPlan(
            { clientId: currentClientId } as Client,
            selectedFacility,
            dispatch,
            state.reduxMasterPayors,
            selectedPayor,
            selectedPlan,
            state.reduxRateAsPayorMap,
            estimate!,
            rateAsPayorPlan,
            vob!,
            updatedEstimate!,
            vob?.vobId
        );
    };
    

    // Handle estimate selection change
    const handleEstimateChange = (event: any) => {
        const newEstimateId = event.target.value;
        
        // Navigate to the selected estimate
        if (newEstimateId && newEstimateId !== estimateId) {
            // Clear the encounter before fetching the new estimate
            dispatch(clearEncounter());
            // Clear selected VOB to maintain state consistency
            dispatch(clearSelectedVob());
            
            // Clear facility levels of care to prevent stale data from previous estimate
            dispatch(estimatorSlice.actions.setFacilityLevelsOfCareList(null));
            
            // Set loading flag
            setIsLoadingEstimate(true);
            
            // Fetch the selected estimate immediately to update the UI
            dispatch(
                loadSelectedEstimate({
                    paramId: paramId, 
                    estimationId: Number(newEstimateId)
                })
            ).finally(() => {
                setIsLoadingEstimate(false);
            });
            
            // Update the URL using React Router's navigate
            navigate(`/patients/${selectedPatient.patientId}/estimate/${newEstimateId}`);
        }
    };

    const handleVobChange = async (event: any) => {
        const newVobId = event.target.value;

        let updatedEstimate: Estimate | null = estimate;
    
        isManualVobChange.current = true;
        
        setSelectedVob(newVobId);

        // dispatch(estimatorSlice.actions.setRateAsPayorPlan(null));
        
        // After initial load, update the current estimate to be associated with the new VOB
        if (newVobId && newVobId !== vob?.vobId?.toString() && !isInitialLoad.current && estimate) {
            try {
                // Update the estimate to be associated with the new VOB
                const putEstimateBody = {
                    ..._.omit(estimate, [
                        'estimateId',
                        'createUserId',
                        'createDt',
                        'lastUpdateUserId',
                        'lastUpdateDt',
                        'summary',
                        'crossOverSummary',
                        'priorCareSummary',
                    ]),
                    patientId: selectedPatient.patientId,
                    vobId: Number(newVobId), // Associate with the new VOB
                    description: normalizeDescription(estimate?.description, estimate.isPrivatePay || isPrivatePay, isSelfPay),
                    crossoverSelection: 0, // Default to "Before Crossover"
                    isPlanYearCrossover: estimate.isPlanYearCrossover || false, // Preserve existing value or default to false
                    advisorPatientId: estimate.advisorPatientId || 1,
                    ...(rateAsPayorPlan != null ? { rateAsPayorPlan } : {}),
                    isSelfPay: isSelfPay,
                    isPrivatePay: estimate.isPrivatePay || isPrivatePay
                };
                updatedEstimate = putEstimateBody;
                await dispatch(
                    (isSelfPay || isPrivatePay)
                    ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                    : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                );

                // Refresh the saved estimates list to show updated description
                await dispatch(loadSavedEstimatesListv3({
                    paramId: paramId,
                    patientId: Number(patientId),
                }));
                
                dispatch(showStatus(`Estimate ${estimate.estimateId} associated with new VOB`));
            } catch (error) {
                console.error('Error updating estimate with new VOB:', error);
                dispatch(showErrorStatus('Error associating estimate with new VOB. Please try again.'));
            }
        }
        
        // Load the new VOB data regardless of initial load status
        if (newVobId && newVobId !== vob?.vobId?.toString()) {
                                const fetchVobAndSetRateAsPayorPlan = async () => {
                try {
                    // First fetch the VOB
                    const vobResponse = await dispatch(loadSelectedVob({vobId: newVobId}));
                    const updatedVob: Vob = vobResponse.payload;
                    const existingVob = Utils.deepClone(vob)

                    // Then fetch facility payers and set rateAsPayorPlan
                    if (updatedVob.facility) {
                        // Use the utility function to load and set the rateAsPayorPlan
                        await loadAndSetRateAsPayorPlan(
                            updatedVob.client,
                            updatedVob.facility,
                            dispatch,
                            state.reduxMasterPayors,
                            updatedVob.payor,
                            updatedVob.plan,
                            state.reduxRateAsPayorMap,
                            estimate!,
                            rateAsPayorPlan,
                            existingVob!,
                            updatedEstimate!,
                            newVobId
                        );
                    }
                    
            
                    isManualVobChange.current = false;
                } catch (error) {
                    console.error('Error fetching VOB or setting rateAsPayorPlan:', error);
                    dispatch(showErrorStatus('Error loading VOB data. Please try again.'));
                
                    isManualVobChange.current = false;
                }
            };
            
            fetchVobAndSetRateAsPayorPlan();
        } else {
        
            isManualVobChange.current = false;
        }
    };

    const handleNewEstimate = () => {
        hasAutoSelectedFirstEstimate.current = true;
        navigate(`/patients/${selectedPatient.patientId}/estimate`, { replace: true });
        setIsPrivatePay(false);
        dispatch(clearSelectedEstimate());
    };

    const handleCompletePatientModalCancel = () => {
        setIsCompletePatientModalOpen(false);
    };

    const handleCompletePatientModalSubmit = async (estimation: any) => {
        setIsCompletePatientModalOpen(false);
        // Refresh data if needed after successful submission
        if (estimation && estimation.estimateId) {
            await dispatch(
                loadSelectedEstimate({
                    paramId: paramId, 
                    estimationId: estimation.estimateId
                })
            );

            if (selectedPatient?.patientId) {
                await dispatch(loadSavedEstimatesListv3({
                    paramId: paramId,
                    patientId: Number(selectedPatient.patientId),
                }));
            }
            dispatch(showStatus(`Instance of Care created successfully`));
        }
    };

    // Prepare for opening the complete patient record modal
    const prepareAndOpenCompletePatientModal = () => {
        // Validate estimate before opening IOC modal
        if (!estimate) {
            dispatch(showErrorStatus('No estimate available for IOC creation'));
            return;
        }

        const validationContext = {
            estimate,
            selectedPatientCurrentInsurance,
            selectedEncounter,
            vob,
            rateAsPayorPlan,
            payersMap,
            facilityLevelsOfCareList
        };

        const validationResult = EstimateValidationService.validateEstimateForIOC(validationContext, !!estimate?.patientEncounterId);

        // Show validation errors and block IOC modal opening if errors exist
        if (validationResult.errors.length > 0) {
            validationResult.errors.forEach(error => {
                dispatch(showErrorStatus(error));
            });
            return; // Validation failed, don't open the modal
        }

        // Show warnings but allow IOC modal to open
        if (validationResult.warnings.length > 0) {
            validationResult.warnings.forEach(warning => {
                dispatch(showErrorStatus(warning));
            });
        }

        // If the estimate has a patientEncounterId but it doesn't match the current selectedEncounter
        // or if the estimate doesn't have a patientEncounterId but there is a selectedEncounter
        if ((estimate?.patientEncounterId && (!selectedEncounter || selectedEncounter.patientEncounterId !== estimate.patientEncounterId)) ||
            (!estimate?.patientEncounterId && selectedEncounter)) {
            
            // Clear the encounter to ensure we don't use stale data
            dispatch(clearEncounter());
            
            // If the estimate has a patientEncounterId, fetch the correct encounter
            if (estimate?.patientEncounterId) {
                patientService.getPatientInstanceOfCare({ 
                    patientId: selectedPatient.patientId, 
                    encounterId: estimate.patientEncounterId 
                }).then(res => {
                    dispatch(setSelectedEncounter(res.entity));
                    setIsCompletePatientModalOpen(true);
                }).catch(error => {
                    console.error('Error fetching patient encounter:', error);
                    dispatch(showErrorStatus('Error loading patient encounter data.'));
                    setIsCompletePatientModalOpen(true);
                });
            } else {
                // If no patientEncounterId, just open the modal with cleared encounter
                setIsCompletePatientModalOpen(true);
            }
        } else {
            // If everything is already in sync, just open the modal
            setIsCompletePatientModalOpen(true);
        }
    };

    useEffect(() => {
        const loadLatestNotManagedIOC = async () => {
            if (!selectedEncounter || !selectedEncounter.patientEncounterId) {
                if (estimate?.patientEncounterId) {
                    return;
                }

                if (isLoadingIOCRef.current) {
                    return;
                }

                const patientId = selectedPatient?.patientId;
                
                if (!patientId) {
                    return;
                }
                if (lastLoadedPatientIdRef.current === patientId) {
                    return;
                }

                isLoadingIOCRef.current = true;
                lastLoadedPatientIdRef.current = patientId;

                try {
                    const response = await patientService.getPatientInstancesOfCare({
                        patientId: patientId,
                        limit: 100,
                        offset: 0,
                    });

                    if (response.hasErrors) {
                        console.error('Error fetching patient encounters:', response.errorMessage);
                        isLoadingIOCRef.current = false;
                        lastLoadedPatientIdRef.current = undefined;
                        return;
                    }

                    const encounters = response.entity as PatientEncounter[];

                    const notManagedEncounters = encounters.filter((encounter) => {
                        const workflowStatusDesc = encounter.workflow?.workflowStatus?.workflowStatusDesc;
                        const workflowStatusId = encounter.workflow?.workflowStatus?.workflowStatusId;
                        return workflowStatusDesc === 'Not Managed' || workflowStatusId === 20;
                    });

                    if (notManagedEncounters.length > 0) {
                        const latestNotManagedIOC = [...notManagedEncounters].sort((a, b) => {
                            const dateA = new Date(a.instanceCreateDt || 0).getTime();
                            const dateB = new Date(b.instanceCreateDt || 0).getTime();
                            if (dateB !== dateA) {
                                return dateB - dateA;
                            }
                            return (b.patientEncounterId || 0) - (a.patientEncounterId || 0);
                        })[0];

                        await dispatch(getInstanceOfCare({
                            patientId: latestNotManagedIOC.patientId,
                            encounterId: latestNotManagedIOC.patientEncounterId,
                        }));
                    }
                } catch (error) {
                    console.error('Error loading latest not managed IOC:', error);
                    lastLoadedPatientIdRef.current = undefined;
                } finally {
                    isLoadingIOCRef.current = false;
                }
            }
        };

        loadLatestNotManagedIOC();
    }, [selectedPatient?.patientId, selectedEncounter?.patientEncounterId, estimate?.patientEncounterId, dispatch]);

    const estimateEncounterId = estimate?.patientEncounterId;
    const selectedEncounterId = selectedEncounter?.patientEncounterId;
    // Fetch the patient encounter if estimate has patientEncounterId but selectedEncounter is empty
    useEffect(() => {
        const encounterIdToFetch = estimateEncounterId;
        const needFetch =
            encounterIdToFetch &&
            selectedEncounterId !== encounterIdToFetch &&
            selectedPatient?.patientId &&
            !isLoadingEstimate &&
            !estimatorLoadingState?.selectedEstimate;

        if (!needFetch) {
            return;
        }
        if (fetchingEncounterIdRef.current === encounterIdToFetch) {
            return;
        }
        fetchingEncounterIdRef.current = encounterIdToFetch;
        const fetchPatientEncounter = async () => {
            try {
                const res = await patientService.getPatientInstanceOfCare({
                    patientId: selectedPatient!.patientId,
                    encounterId: encounterIdToFetch!,
                });
                dispatch(setSelectedEncounter(res.entity));
            } catch (error) {
                console.error('Error fetching patient encounter:', error);
                dispatch(showErrorStatus('Error loading patient encounter data.'));
            } finally {
                if (fetchingEncounterIdRef.current === encounterIdToFetch) {
                    fetchingEncounterIdRef.current = null;
                }
            }
        };
        fetchPatientEncounter();
    }, [estimateEncounterId, selectedEncounterId, selectedPatient?.patientId, dispatch, isLoadingEstimate, estimatorLoadingState?.selectedEstimate]);


    // Navigate to new estimate URL when a new estimate is created
    useEffect(() => {
        if (
            estimate?.quoteMethod === QuoteMethod.MANUAL ||
            estimate?.quoteMethod === QuoteMethod.SCA
        ) {
            setDoesLOCSummaryValNeedUpdating(true);
        }
        if (estimate?.estimateId && selectedPatient?.patientId && !estimateId) {
            console.log('New estimate created, navigating to include estimate ID in URL:', estimate.estimateId);
            navigate(`/patients/${selectedPatient.patientId}/estimate/${estimate.estimateId}`, { replace: true });
        }
    }, [estimate?.estimateId, selectedPatient?.patientId, estimateId, navigate]);


    const handleCopyEstimate = async () => {
        if (!estimate) {
            dispatch(showErrorStatus('No estimate to copy'));
            return;
        }

        try {
            setIsCopying(true);
            
            const createdDate = admissionsAdvisorUtils.formatDateTime(new Date());
            const levelsOfCare = estimate.facilityLevelOfCare
                ?.map((loc) => loc.facilityLevelOfCareName)
                .join(', ');
            const estimateDescription = `${levelsOfCare} - ${createdDate}`;

            const estimateCopy = {
                vobId: estimate.vobId,
                clientId: estimate.clientId,
                facilityId: estimate.facilityId,
                advisorPatientId: estimate.advisorPatientId,
                patientId: selectedPatient.patientId,
                ...((selectedEncounter?.patientEncounterId || estimate.patientEncounterId) && { 
                    patientEncounterId: selectedEncounter?.patientEncounterId || estimate.patientEncounterId 
                }),
                description: estimateDescription,
                quoteMethod: estimate.quoteMethod,
                anticipatedAdmitDate: estimate.anticipatedAdmitDate,
                facilityLevelOfCare: estimate.facilityLevelOfCare?.map(loc => {
                    const { totalCharge, rate, ...cleanLoc } = loc as any;
                    return cleanLoc;
                }) || [],
                isPlanYearCrossover: estimate.isPlanYearCrossover || false,
                summarySelection: estimate.summarySelection,
                crossoverSelection: estimate.crossoverSelection,
                isActive: true,
                rateAsPayorPlan: rateAsPayorPlan,
                isSelfPay: isSelfPay,
                isPrivatePay: estimate.isPrivatePay,
                ...(estimate.priorCare && { priorCare: estimate.priorCare }),
                ...(estimate.financialAssistance && { financialAssistance: estimate.financialAssistance }),
            };


            const createAction = (isSelfPay || estimate.isPrivatePay) ? createSelectedEstimateSelfPay : createSelectedEstimate;
            const response = await dispatch(createAction(estimateCopy));
            
            if (response.meta.requestStatus === 'fulfilled') {
                const newEstimateId = response.payload?.estimateId;
                
                if (newEstimateId) {
                    // refresh list
                    await dispatch(loadSavedEstimatesListv3({
                        paramId: paramId,
                        patientId: Number(selectedPatient?.patientId),
                    }));

                    await dispatch(loadSelectedEstimate({
                        paramId: paramId,
                        estimationId: newEstimateId
                    }));
                    
                    // navigate to new estimate
                    navigate(`/patients/${selectedPatient?.patientId}/estimate/${newEstimateId}`);
                } else {
                    dispatch(showErrorStatus('Failed to get new estimate ID'));
                }
            } else {
                dispatch(showErrorStatus('Failed to copy estimate'));
            }
        } catch (error) {
            console.error('Error copying estimate:', error);
            dispatch(showErrorStatus(`Error copying estimate: ${error}`));
        } finally {
            setIsCopying(false);
        }
    };

    const handleSaveEstimate = async (privatePayStatus: boolean, hideSuccessMsg: boolean) => {
        if ((!vob || !vob.vobId) && (!isSelfPay && !privatePayStatus)) {
            dispatch(showErrorStatus('Cannot save estimate without a valid VOB. Please ensure a VOB is selected.'));
            return;
        }

        if (!estimate) {
            dispatch(showErrorStatus('No estimate to save. Please create an estimate first.'));
            return;
        }        

        // Validate estimate before saving
        const validationContext = {
            estimate,
            selectedPatientCurrentInsurance,
            selectedEncounter,
            vob,
            rateAsPayorPlan,
            payersMap,
            facilityLevelsOfCareList
        };

        const validationResult = EstimateValidationService.validateEstimateOnSave(validationContext);

        // Show validation errors and block save if errors exist
        if (validationResult.errors.length > 0) {
            validationResult.errors.forEach(error => {
                dispatch(showErrorStatus(error));
            });
            return;
        }

        // Show warnings but allow save to continue
        if (validationResult.warnings.length > 0) {
            validationResult.warnings.forEach(warning => {
                dispatch(showErrorStatus(warning));
            });
        }

        setIsSaving(true);
        try {
            const putEstimateBody = {
                ..._.omit(estimate, [
                    'estimateId',
                    'createUserId',
                    'createDt',
                    'lastUpdateUserId',
                    'lastUpdateDt',
                    'summary',
                    'crossOverSummary',
                    'priorCareSummary',
                ]),
                patientId: selectedPatient.patientId,
                vobId: vob?.vobId!,
                advisorPatientId: estimate?.advisorPatientId || 1,
                rateAsPayorPlan: rateAsPayorPlan,
                isSelfPay: isSelfPay,
                isPrivatePay: privatePayStatus,
                description: normalizeDescription(estimate?.description, privatePayStatus, isSelfPay)
            };
            await dispatch(
                (isSelfPay || privatePayStatus)
                  ? saveSelectedEstimateSelfPay({ estimateId: estimate.estimateId, estimate: putEstimateBody })
                  : saveSelectedEstimate({ estimateId: estimate.estimateId, estimate: putEstimateBody })
            );
            
            // Refresh the saved estimates list to show updated description
            await dispatch(loadSavedEstimatesListv3({
                paramId: paramId,
                patientId: Number(patientId),
            }));
            
            if (hideSuccessMsg) return;
            dispatch(
                showStatus(
                  `Estimate ${estimate.estimateId} saved successfully`
                )
            );
        } catch (error) {
            console.error('Error saving estimate:', error);
            dispatch(showErrorStatus('Error saving estimate. Please try again.'));
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrivatePay = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const privatePayStatus = event.target.checked;
        setIsPrivatePay(privatePayStatus);
        let updatedEstimate: Estimate = {
            ...Utils.deepClone(estimate),
            isPrivatePay: privatePayStatus,
        };
        const desc = updatedEstimate.description || "";
        // if Private pay, we want the description to change.
        if (privatePayStatus) {
            // Add prefix if missing
            if (!desc.startsWith("Private Pay - ")) {
              updatedEstimate.description = `Private Pay - ${desc}`;
            }
            // For Private Pay Estimate - We need to call LoadAndSet w/ Self Pay Payor
            // call the rateasPayor w/ new facility
            const sssconfig: any = {
                paramId: paramId,
                masterListPayers: payers,
                facilityId: estimate?.facilityId
                    ? estimate.facilityId
                    : vob?.facility?.facilityId,
            };
            const facilityPayors = await dispatch(getEstimatorFacilityPayers(sssconfig));
            const selfPayPayor = getSelfPayPayer(facilityPayors);
            const selectedPlan = getPlanDetails(selfPayPayor, facilityPayors);
            let updatedPatientCurrentInsurance = {
                ...selectedPatientCurrentInsurance,
                payorId: selfPayPayor?.cfgPayorId,
                payorPlanId: selectedPlan?.payorPlanId,
            }
            // Create an adapter function with the signature expected by loadAndSetRateAsWithoutVob
            const loadAndSetRateAsPayorPlanAdapter = async (
                facility: any,
                dispatch: Dispatch<any>,
                payers: any,
                payor: Payor,
                plan: Plan
            ): Promise<void> => {
                const clientId = estimate?.clientId || selectedPatient?.clientId ? Number(estimate?.clientId || selectedPatient?.clientId) : 0;
                const clientObj = { clientId } as Client;
                if (!estimate?.facilityId) {
                    console.error('Estimate does not have a facilityId');
                    return;
                }
                const targetClient = allClients.get(Number(clientId));
                const facilityFromClient = targetClient?.clientFacilities?.find(
                    (f: any) => f.facilityId === Number(estimate.facilityId)
                );
                const estimateFacility: Facility = facilityFromClient 
                    ? { 
                        facilityId: facilityFromClient.facilityId, 
                        facilityName: facilityFromClient.facilityName 
                    } as Facility
                    : { facilityId: Number(estimate.facilityId) } as Facility;
                await loadAndSetRateAsPayorPlan(
                    clientObj,
                    estimateFacility,
                    dispatch,
                    state.reduxMasterPayors,
                    payor,
                    plan,
                    state.reduxRateAsPayorMap,
                    estimate!,
                    rateAsPayorPlan,
                    vob!,
                    updatedEstimate,
                    vob?.vobId
                );
            };

            await loadAndSetRateAsWithoutVob({
                selectedPatient,
                patientDemographicResp: null,
                selectedPatientCurrentInsurance: updatedPatientCurrentInsurance,
                patientInsuranceResp: null,
                allClientsMap: allClients,
                dispatch,
                payers,
                loadAndSetRateAsPayorPlan: loadAndSetRateAsPayorPlanAdapter
            });
        } else {
            // Remove prefix if present
            if (desc.startsWith("Private Pay - ")) {
              updatedEstimate.description = desc.replace(/^Private Pay - /, "");
            }
            const vobFacilityId = vob?.facility?.facilityId;
            const estimateFacilityId = estimate?.facilityId;

            const facilityToUse = vobFacilityId || estimateFacilityId;

            if (vobFacilityId && vobFacilityId !== estimateFacilityId) {
                updatedEstimate.facilityId = vobFacilityId;
            }

            let facilityObj: Facility;
            if (facilityToUse && currentClientId) {
                const targetClient = allClients.get(Number(currentClientId));
                const facilityFromClient = targetClient?.clientFacilities?.find(
                    (f: any) => f.facilityId === Number(facilityToUse)
                );
                facilityObj = facilityFromClient 
                    ? { 
                        facilityId: facilityFromClient.facilityId, 
                        facilityName: facilityFromClient.facilityName 
                    } as Facility
                    : vob?.facility || { facilityId: Number(facilityToUse) } as Facility;
            } else {
                facilityObj = vob?.facility || { facilityId: Number(facilityToUse) } as Facility;
            }
            
            // Create a default client object if vob.client is undefined
            const clientObj = vob?.client || { clientId: currentClientId } as Client;
            await loadAndSetRateAsPayorPlan(
                clientObj,
                facilityObj,
                dispatch,
                state.reduxMasterPayors,
                vob?.payor || {} as Payor,
                vob?.plan || {} as Plan,
                state.reduxRateAsPayorMap,
                estimate!,
                rateAsPayorPlan,
                vob!,
                updatedEstimate,
                vob?.vobId
            );
          }
        dispatch(setSelectedEstimate(updatedEstimate)) // setting state
      };

    return (
        <>
			{isLoadingClients ? (
				<LoadingOverlay />
			) : (
                <>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: 24,
                            height: 'auto',
                            overflow: 'visible',
                            backgroundColor: '#F5F7F9',
                        }}
                        test-id="estimate-tab-container"
                    >
                        <Grid
                            container
                            justifyContent="space-between"
                            direction="row"
                            spacing={2}
                        >
                            <Grid item direction="column" xs={8} test-id="estimate-tab-left-column">
                                <Paper test-id="estimate-tab-details-panel">
                                    <Box
                                        style={{
                                            padding: '1.0em 1.0em 0.5em 1.0em',
                                            marginBottom: '0.5em',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Grid container spacing={2} direction="row">
                                            <Grid item xs={3}>
                                                <Typography
                                                    variant="caption"
                                                    style={{color: 'grey'}}
                                                    test-id="estimate-tab-client-label"
                                                >
                                                    Client
                                                </Typography>
                                                <Typography variant="h3" test-id="estimate-tab-client-value">
                                                    {currentClient?.clientName}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={4} container justifyContent="flex-end">
                                                <Grid item xs={12}>
                                                    {(isPrivatePay || isSelfPay) ? (
                                                         <Box width="100%">
                                                            <TextField
                                                                select
                                                                label={
                                                                facilitiesLoading || (!selectedFacility && facilities.length === 0)
                                                                    ? "Loading..."
                                                                    : "Facility"
                                                                }
                                                                onBlur={
                                                                    createEstimatorFormik.handleBlur
                                                                }
                                                                name="facility"
                                                                value={selectedFacility || ""}
                                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                                handleFacilityChange(Number(e.target.value))
                                                                }
                                                                disabled={facilitiesLoading || (facilities.length === 0 && !selectedFacility)}
                                                                error={Boolean(facilities.length === 0 && !facilitiesLoading)} // optional error handling
                                                                test-id="vob-facility-select"
                                                            >
                                                                {facilitiesLoading || (facilities.length === 0 && !selectedFacility) ? (
                                                                <MenuItem disabled>Loading facilities...</MenuItem>
                                                                ) : (
                                                                facilities.map((facility: any) => (
                                                                    <MenuItem key={facility.facilityId} value={facility.facilityId}>
                                                                    {facility.facilityName}
                                                                    </MenuItem>
                                                                ))
                                                                )}
                                                            </TextField>  
                                                        </Box>                                                        
                                                        ) : (
                                                            <>
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{color: 'grey'}}
                                                                    test-id="estimate-tab-facility-label"
                                                                >
                                                                    Facility
                                                                </Typography>
                                                                <Typography variant="h3" test-id="estimate-tab-facility-value">
                                                                    {currentFacility?.facilityName}
                                                                </Typography>
                                                            </>
                                                        )}
                                                </Grid>
                                            </Grid>

                                            <Grid item xs={4} container justifyContent="flex-end">
                                                <Box width="100%">
                                                    <TextField
                                                        select={true}
                                                        label="Quote Method"
                                                        name="quoteMethod"
                                                        value={quoteMethodValueAsEnum}
                                                        onChange={(e: Event) => {
                                                            createEstimatorFormik.handleChange(
                                                                e
                                                            );
                                                            handleNewEstimateQuoteMethodChange(
                                                                e
                                                            );
                                                        }}
                                                        onBlur={
                                                            createEstimatorFormik.handleBlur
                                                        }
                                                        error={Boolean(
                                                            createEstimatorFormik
                                                                .touched.quoteMethod &&
                                                                createEstimatorFormik
                                                                    .errors.quoteMethod
                                                        )}
                                                        disabled={
                                                            isDisabledAfterConvert
                                                        }
                                                        test-id="estimate-quote-method"
                                                    >
                                                        <MenuItem
                                                            value={QuoteMethod.AVGLOS}
                                                        >
                                                            Average Length of Stay
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={QuoteMethod.RLOS}
                                                        >
                                                            Recommended Length of Stay
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={QuoteMethod.MANUAL}
                                                        >
                                                            Manual
                                                        </MenuItem>
                                                        <MenuItem
                                                            value={QuoteMethod.SCA}
                                                        >
                                                            Single Case Agreement
                                                        </MenuItem>
                                                    </TextField>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                        
                                        <Box display="flex" gap={1}>
                                            <DialogActionButton 
                                                isEnabled={true}
                                                savebuttonText="New"
                                                saveStatus={saveCallbackStatus.none}
                                                executeSave={handleNewEstimate}
                                                handleCallbackSave={() => {}}
                                                handleCallbackCancel={() => {}}
                                                showCancelButton={false}
                                                test-id="estimate-tab-new-estimate-button"
                                            />
                                            <Button
                                                test-id="estimate-tab-save-estimate-button-header"
                                                onClick={() => {
                                                    if (isDisabledAfterConvert) {
                                                        handleCopyEstimate();
                                                    } else {
                                                        handleSaveEstimate(isPrivatePay, false);
                                                    }
                                                }}
                                                disabled={!Boolean(estimate?.estimateId) || isSaving || isCopying}
                                                style={{ 
                                                    minWidth: 'fit-content',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}
                                            >
                                                {isCopying ? 'Copying...' : isSaving ? 'Saving...' : (isDisabledAfterConvert ? 'Copy' : 'Save')}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {/* Estimate dropdown */}
                                    <Box
                                        style={{
                                            padding: '0.5em 1.0em 0 1.0em',
                                            marginBottom: '0.5em',
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    select={true}
                                                    label="Estimate"
                                                    name="estimate"
                                                    value={selectedEstimateValue}
                                                    onChange={handleEstimateChange}
                                                    onBlur={() => {}}
                                                    loading={savedEstimatesLoading}
                                                    test-id="estimate-tab-estimate-dropdown"
                                                >
                                                    {savedEstimations.length > 0 &&
                                                        [...(savedEstimations as any[])]
                                                            .map((est) => (
                                                                <MenuItem
                                                                    key={est.pfrEstimateId}
                                                                    value={est.pfrEstimateId?.toString()}
                                                                    style={{ 
                                                                        fontWeight: est.estimateBody?.patientEncounterId ? 'bold' : 'normal',
                                                                        // If this is associated with the current IOC, add a visual indicator
                                                                        color: est.estimateBody?.patientEncounterId ? '#2E7D32' : 'inherit'
                                                                    }}
                                                                >
                                                                    {est.estimateBody?.description || `Estimate ${est.pfrEstimateId}`}
                                                                    {est.estimateBody?.patientEncounterId && ` - IOC: ${est.estimateBody.patientEncounterId}`}
                                                                </MenuItem>
                                                            ))
                                                    }
                                                </TextField>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* VOB */}
                                    <Box
                                        style={{
                                            padding: '0.5em 1.0em 1.0em 1.0em',
                                            marginBottom: '1.5em',
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            { (!isPrivatePay && !isSelfPay) &&
                                                <Grid item xs={8}>
                                                    <Tooltip 
                                                        title={isSelfPay ? "VOB is not available for self-pay patients" : ""}
                                                        placement="top"
                                                        disableHoverListener={!isSelfPay && !isPrivatePay}
                                                    >
                                                        <div style={{ width: '100%' }}>
                                                            <TextField
                                                                select={true}
                                                                label="VOB"
                                                                name="vob"
                                                                value={selectedVob}
                                                                onChange={handleVobChange}
                                                                onBlur={() => {}}
                                                                loading={patientVobListLoading}
                                                                test-id="estimate-tab-vob-dropdown"
                                                                disabled={(isSelfPay || isPrivatePay) || isDisabledAfterConvert}
                                                            >
                                                                {vobsList.map((vobItem) => (
                                                                    <MenuItem key={vobItem.id} value={vobItem.id}>
                                                                        {vobItem.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </TextField>
                                                        </div>
                                                    </Tooltip>
                                                </Grid>
                                            }
                                            {!isSelfPay &&
                                                <Grid 
                                                    item 
                                                    xs={4}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        paddingTop: '16px'
                                                    }}
                                                >
                                                    <FormGroup>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={isPrivatePay}
                                                                    test-id="estimate-tab-private-pay"
                                                                    onChange={handlePrivatePay}
                                                                    disabled={isDisabledAfterConvert}
                                                                />
                                                            }
                                                            label="Private Pay"
                                                        />
                                                    </FormGroup>
                                                </Grid>
                                            }
                                            
                                        </Grid>
                                    </Box>
                                </Paper>
                                {/*Levels of Care */}
                                <Paper test-id="estimate-tab-loc-panel">
                                    <Box
                                        style={{
                                            padding: '1.0em',
                                            marginBottom: '1.5em',
                                        }}
                                        test-id="estimate-tab-loc-container"
                                    >
                                        <Grid
                                            container
                                            justifyContent="space-between"
                                            direction="row"
                                        >
                                            <Grid
                                                item
                                                direction="column"
                                                xs={12}
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    paddingBottom: '0.5em',
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle2"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}
                                                    test-id="estimate-tab-loc-title"
                                                >
                                                    <PermContactCalendarIcon
                                                        sx={{
                                                            marginRight: '0.25em',
                                                        }}
                                                    />{' '}
                                                    Levels of Care {rateAsPayorPlan && (
                                                        <>- Rate as: {rateAsPayorPlan.payor.facilityPayorName || rateAsPayorPlan.payor.payorName}{rateAsPayorPlan.plan?.planName && `/${rateAsPayorPlan.plan.planName}`} - {rateAsPayorPlan?.payor?.inNetwork ? 'In Network' : 'Out of Network'}</>
                                                    )}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Grid container direction="row">
                                            <Grid item xs={6} direction="column">
                                                <AdmissionDateV3
                                                    admissionDate={initialAdmissionDate}
                                                    onChangeHandler={handleAdmissionDateOnChange}
                                                />
                                            </Grid>
                                            <>
                                                {promptUserToUpdateModalForCrossover && (
                                                    <Grid
                                                        container
                                                        direction="row"
                                                        sx={{
                                                            backgroundColor: '#E8DB47',
                                                            marginY: '0.5rem',
                                                        }}
                                                    >
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            className="p-2"
                                                        >
                                                            <span test-id="estimate-tab-crossover-warning">
                                                                Please open the LOC modal
                                                                and update Days Before
                                                                Crossover value(s)
                                                            </span>
                                                        </Grid>
                                                    </Grid>
                                                )}

                                                <NewLOCSummary
                                                    handleLocModalOpen={() => {
                                                        setIsLOCModalOpen(true)
                                                      }}
                                                    enableLOSbutton={
                                                        !isDisabledAfterConvert
                                                    }
                                                    selectedLevelsOfCare={
                                                        estimate?.facilityLevelOfCare ||
                                                        []
                                                    }
                                                    quoteMethod={quoteMethodValueAsEnum}
                                                    displayValueRed={
                                                        doesLOCSummaryValNeedUpdating
                                                    }
                                                    test-id="estimate-tab-loc-summary"
                                                />
                                            </>
                                        </Grid>
                                    </Box>
                                </Paper>
                                {(!isSelfPay && !isPrivatePay) &&
                                    <EstimatorPriorCareSummary test-id="estimate-tab-prior-care-summary" />
                                }
                                
                                {/* Action buttons under Prior Care */}
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        padding: '20px 0',
                                        backgroundColor: '#F5F7F9'
                                    }}
                                >
                                    <Button 
                                        disabled={!Boolean(estimate) || savedEstimatesLoading}
                                        loading={savedEstimatesLoading}
                                        onClick={() => {
                                            // if encounter exists and is converted, navigate to IOC page
                                            if (isIOCConverted) {
                                                navigate(`/specialist/dashboard/${selectedPatient?.patientId}?encounter=${selectedEncounter.patientEncounterId}`);
                                            } else {
                                                // otherwise open the complete patient modal
                                                prepareAndOpenCompletePatientModal();
                                            }
                                        }}
                                        test-id="estimate-tab-ioc-action-button"
                                    >
                                        {(() => {
                                            // if estimate has no patient encounter ID, show Create IOC
                                            if (!estimate?.patientEncounterId) {
                                                return "Create IOC";
                                            }
                                            
                                            // if encounter exists and is converted, show View Instance of Care
                                            if (isIOCConverted) {
                                                return "View IOC";
                                            }
                                            
                                            // default to Update IOC for existing encounters that aren't converted
                                            return "Update IOC";
                                        })()}
                                    </Button>
                                    <Button 
                                        test-id="estimate-tab-save-estimate-button"
                                        onClick={() => {
                                            if (isDisabledAfterConvert) {
                                                handleCopyEstimate();
                                            } else {
                                                handleSaveEstimate(isPrivatePay, false);
                                            }
                                        }}
                                        disabled={!Boolean(estimate?.estimateId) || isSaving || isCopying}
                                        style={{ 
                                            minWidth: 'fit-content',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}
                                    >
                                        {isCopying ? 'Copying...' : isSaving ? 'Saving...' : (isDisabledAfterConvert ? 'Copy' : 'Save')}
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Side section with summaries */}
                            <Grid item direction="column" xs={4} test-id="estimate-tab-right-column">
                                <Box
                                    border={1}
                                    borderColor="primary.main"
                                    p={2}
                                    style={{
                                        backgroundColor: 'white',
                                        marginBottom: '1.5em',
                                    }}
                                    test-id="estimate-tab-financial-summary-box"
                                >
                                    <Typography variant="subtitle2" test-id="estimate-tab-financial-summary-title">
                                        Financial Summary
                                    </Typography>
                                    <SidebarFinancialSummary test-id="estimate-tab-financial-summary" />
                                </Box>

                                {!isDisabledAfterConvert && (
                                    <SidebarPlanYearCrossover test-id="estimate-tab-plan-year-crossover" />
                                )}

                                <SidebarTotalEstimatedPfr test-id="estimate-tab-total-estimated-pfr" />

                                <SidebarPriorCareAdjustment test-id="estimate-tab-prior-care-adjustment" />

                                <Paper test-id="vob-comments-paper">
                                    <div test-id="vob-comments-container" className="px-4 py-4">
                                        <EstimatesSidebar
                                            clientId={selectedPatient?.clientId ? Number(selectedPatient.clientId) : undefined}
                                            clientEmployerId={selectedPatient?.contact?.contactIncome?.clientEmployerId}
                                            payorId={selectedPatientCurrentInsurance?.payorId}
                                            groupNumber={selectedPatientCurrentInsurance?.groupNumber}
                                            memberId={selectedPatientCurrentInsurance?.memberId}
                                        />
                                    </div>
                                </Paper>
                            </Grid>
                        </Grid>
                    </div>
                    {isLOCModalOpen && (
                        <EstimatorLevelOfCareModal
                            open={isLOCModalOpen}
                            clientId={createEstimatorFormik.values.client}
                            facilityId={createEstimatorFormik.values.facility}
                            facilityName={
                                currentFacility?.facilityName || ''
                            }
                            quoteMethod={createEstimatorFormik.values.quoteMethod}
                            admissionDate={admissionDate}
                            facilityLevelsOfCareList={facilityLevelsOfCareList || []}
                            handleEstimatorLevelOfCareModalClose={
                                handleEstimatorLevelOfCareModalClose
                            }
                            promptUserToUpdateModalForCrossover={
                                promptUserToUpdateModalForCrossover
                            }
                            test-id="estimate-tab-loc-modal"
                        />
                    )}

                    <TakePaymentModal
                        paymentData={paymentData}
                        open={openPaymentModal}
                        handleModalCancel={handlePaymentModalCancel}
                        test-id="estimate-tab-payment-modal"
                    />

                    <EstCompletePatientRecordModal
                        key={`patient-record-modal-${estimate?.estimateId}-${selectedEncounter?.patientEncounterId || 'new'}`}
                        open={isCompletePatientModalOpen}
                        handlePatientRecordModalCancel={handleCompletePatientModalCancel}
                        handlePatientRecordModalSubmit={handleCompletePatientModalSubmit}
                        test-id="estimate-tab-complete-patient-modal"
                    />
                </>
            )}
		</>
    );
}

export default EstimateTab;
